import { TFunction } from 'i18next';
import { Moment as MomentType } from 'moment';
import momentTz, { Moment, MomentInput } from 'moment-timezone';
import {
  DATE_FORMAT_FULL_MONTH,
  DATE_FORMAT_FULL_MONTH_WITH_TIME,
  DATE_FORMAT_FULL_WITH_HOURS_MINS,
  DATE_FORMAT_FULL_WITH_WEEKDAY,
  DATE_FORMAT_WITH_COMMA_HOUR_MINS_WITH_TIMEZONE,
  TIMEZONE_ABBREVIATION,
  displayDate,
  displayDateTz,
  parseISOZonedDate,
} from 'utils/date';
import { guessCurrentTimeZone } from 'utils/datetime';
import { getCustomUnitFormat } from 'utils/l10n/locale';
import { Nullable } from 'utils/tsHelpers';

const MINS_IN_HOURS = 60;
const MINS_IN_DAY = 1440;
const FORMAT_24_HOURS = 'hh:mm A';
const fmtDays = getCustomUnitFormat('day', 'long');
const fmtHours = getCustomUnitFormat('hour', 'long');
const fmtMinutes = getCustomUnitFormat('minute', 'long');

export const abbreviateTimeZone = (timeZone: string) =>
  momentTz().tz(timeZone).format(TIMEZONE_ABBREVIATION);

export const calculateTimeZoneOffset = (
  timezone1: string,
  timezone2: string,
  timestamp: Moment,
) => {
  // Create moments for the current date and time in the given timezones
  const time1 = momentTz.tz(timestamp, timezone1);
  const time2 = momentTz.tz(timestamp, timezone2);

  // Calculate the offset in minutes between the two timezones
  const offsetInMinutes = time1.utcOffset() - time2.utcOffset();

  // Calculate the offset hours and minutes
  const offsetHours = Math.floor(offsetInMinutes / 60);
  const offsetMinutes = Math.abs(offsetInMinutes % 60);

  return { offsetHours, offsetMinutes };
};

/**
 * Converts a UTC timestamp from the current time zone to the desired time zone.
 *
 * @param {string} utcTimestamp - The UTC timestamp to be converted.
 * @param {string} desiredTimeZone - The desired time zone, e.g., 'America/Los_Angeles'.
 * @param {string} currentTimeZone - The current time zone, e.g., 'America/New_York'.
 * @returns {string} The converted UTC timestamp in the desired time zone.
 *
 * @example
 * // Convert a UTC timestamp from New York (EST) to Los Angeles (PST).
 * const utcTimestamp = "2023-10-28T04:00:00.000Z"; // Input UTC timestamp
 * const desiredTimeZone = "America/Los_Angeles"; // Desired time zone (PST)
 * const currentTimeZone = "America/New_York"; // Current time zone (EST)
 * const utcTimestampPST = convertTimeZone(utcTimestamp, desiredTimeZone, currentTimeZone);
 * console.log(utcTimestampPST); // Output: "2023-10-28T07:00:00.000Z" (UTC timestamp in PST)
 */
export const convertTimeZone = (
  utcTimestamp: string,
  desiredTimeZone: string,
  currentTimeZone = guessCurrentTimeZone(),
) => {
  // Parse the UTC timestamp into a Moment object
  const momentObjOriginal = momentTz.utc(utcTimestamp);
  // Compute the offset between the two timezones
  const { offsetHours, offsetMinutes } = calculateTimeZoneOffset(
    currentTimeZone,
    desiredTimeZone,
    momentObjOriginal,
  );

  // Add the time difference to the original timestamp
  const momentObjConverted = momentObjOriginal
    .add(offsetHours, 'hours')
    .add(offsetMinutes, 'minutes');

  return momentObjConverted;
};

export const convertTimezoneInZonedDateTime = (zonedTimestamp: string, timeZone: string) => {
  const ISOZonedDate = parseISOZonedDate(zonedTimestamp) as Moment;
  const convertedTimestamp = convertTimeZone(
    ISOZonedDate.toISOString(),
    timeZone,
    ISOZonedDate.tz(),
  );

  return `${convertedTimestamp.toISOString()}[${timeZone}]`;
};

export const getFormattedDateFromISOZonedDateWithTimeZone = (
  datetime: Nullable<string>,
  abbreviate = false,
) => {
  if (!datetime) return;
  const ISOZonedDate = parseISOZonedDate(datetime) as Moment;
  const timeZone = abbreviate ? abbreviateTimeZone(ISOZonedDate.tz() || '') : ISOZonedDate.tz();
  return `${displayDate(ISOZonedDate, DATE_FORMAT_FULL_MONTH_WITH_TIME)} (${timeZone})`;
};

export const getTimezoneFromISOZonedDate = (datetime: Nullable<string>) => {
  if (!datetime) return;
  const ISOZonedDate = parseISOZonedDate(datetime) as Moment;
  return ISOZonedDate.tz();
};

export const getFormattedDateFromISOZonedDate = (datetime: Nullable<string>) => {
  if (!datetime) return;
  const ISOZonedDate = parseISOZonedDate(datetime);
  return displayDate(ISOZonedDate, DATE_FORMAT_FULL_MONTH_WITH_TIME);
};

export const getOnlyDateFormattedFromISOZonedDate = (datetime: Nullable<string>) => {
  if (!datetime) return;
  const ISOZonedDate = parseISOZonedDate(datetime);
  return ISOZonedDate.startOf('day');
};

export const getOnlyTimeFormattedFromISOZonedDate = (datetime: Nullable<string>) => {
  if (!datetime) return;
  const ISOZonedDate = parseISOZonedDate(datetime);
  return ISOZonedDate.format(FORMAT_24_HOURS);
};

export const getFormattedFullDateWithHoursAndMins = (datetime: MomentInput, timezone: string) =>
  displayDateTz(datetime, `${DATE_FORMAT_FULL_WITH_HOURS_MINS} z`, timezone);

export const formatSendTimeDateTimeStamp = (date: string, time: string, timeZone: string) => {
  const dateValue = momentTz(date).format(DATE_FORMAT_FULL_MONTH);
  return `${dateValue}, ${time || ''} ${timeZone}`;
};

export const getDateTimeStampFromSeparateDateAndTime = (
  date: MomentType,
  time: string,
  desiredTimeZone: string,
) => {
  // The date picker returns in the users local TZ. So, we need to convert this first to the selected TZ
  const convertDateToDesiredTimeZone = convertTimeZone(date.toISOString(), desiredTimeZone);

  // Converting the strings to moment objects
  const utcMomentDate = momentTz.utc(convertDateToDesiredTimeZone);
  const timeMoment = momentTz(time, FORMAT_24_HOURS);

  // Convert the ISO date time to the start day of the specified timezone
  const utcToTimezoneDate = utcMomentDate.tz(desiredTimeZone);
  const startOfTimezoneDate = utcToTimezoneDate.startOf('day');

  // Extract the hours and minutes from the timeMoment
  const hoursToAdd = timeMoment.hours();
  const minutesToAdd = timeMoment.minutes();

  // Adding the hour and mins to the specified date
  return startOfTimezoneDate.add(hoursToAdd, 'hours').add(minutesToAdd, 'minutes');
};

export const addMinsToFormattedFullDateWithHoursAndMins = (
  addMins: number,
  datetime: MomentInput,
  timezone: string,
) => {
  const futureTime = momentTz(datetime).add(addMins, 'm');
  return getFormattedFullDateWithHoursAndMins(futureTime, timezone);
};

export const getFormattedDateWithHoursAndMins = (datetime: MomentInput, timezone: string) =>
  displayDateTz(datetime, DATE_FORMAT_WITH_COMMA_HOUR_MINS_WITH_TIMEZONE, timezone);

export const getFormattedDateWithWeekDayFromEpoch = (epoch: number, timezone: string) =>
  displayDateTz(new Date(epoch), `${DATE_FORMAT_FULL_WITH_WEEKDAY} z`, timezone);

export const getFormattedTime = (datetime: MomentInput, timezone: string) =>
  displayDateTz(datetime, 'h:mm A', timezone);

const divModMinutes = (mins: number): number[] => [
  Math.floor(mins / MINS_IN_DAY),
  Math.floor((mins % MINS_IN_DAY) / MINS_IN_HOURS),
  mins % MINS_IN_HOURS,
];

export const humanizeMinutesDisplay = (t: TFunction, totalMinutes: number) => {
  const conjunction = t('humanizeMinutes.conjunction');
  const [days, hours, mins] = divModMinutes(totalMinutes);

  // Remove any null time units
  const timeLeft = [
    days ? fmtDays.format(days) : null,
    hours ? fmtHours.format(hours) : null,
    mins ? fmtMinutes.format(mins) : null,
  ].filter((timeUnit) => timeUnit !== null);

  // Replicating Intl.ListFormat('en', { style: 'long', type: 'conjunction' }) fuctionality
  // Replace once our app's tsconfigs target is updated to ES2021
  const lastTimeUnit = timeLeft.slice(-1)[0];
  const restOfTheTimeUnits = timeLeft.slice(0, -1);
  const joinLastElement = timeLeft.length < 2 ? '' : ` ${conjunction} `;

  return (
    [restOfTheTimeUnits.join(', '), lastTimeUnit].join(joinLastElement) ||
    t('humanizeMinutes.default')
  );
};
